import { useEffect, useState } from 'react';
import { api, getToken } from '../../lib/api';
import { day } from '../../lib/format';
import { Card, Empty, ErrorNote, Loading, useResource, useToast } from '../../components/ui';
import { plural, type PointRow } from './types';

interface PolicyVersions {
  current: { version: number; contentHash: string; publishedAt: string };
  versions: Array<{ version: number; contentHash: string; publishedAt: string; cookies: number }>;
}

/** The published cookie policy: its public address, versions, and the page as visitors see it. */
export default function Policy({ points, reload }: { points: PointRow[]; reload: number }) {
  const toast = useToast();
  const versions = useResource<PolicyVersions>(() => api.get('/v1/cookie-policy/versions'), [reload]);
  const banners = points.filter((p) => p.channel === 'cookie' && p.apiKeyPrefix);
  const [bannerId, setBannerId] = useState('');
  const banner = banners.find((b) => b.id === bannerId) ?? banners[0] ?? null;
  const address = banner ? `${window.location.origin}/v1/cookie-policy?k=${banner.apiKeyPrefix}.YOUR_KEY_SECRET` : '';

  const [html, setHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // The preview is HTML, not JSON, so it is fetched directly with the console session.
  useEffect(() => {
    let cancelled = false;
    setPreviewError(null);
    fetch('/v1/cookie-policy/preview', {
      headers: { Authorization: `Bearer ${getToken() ?? ''}` },
      signal: AbortSignal.timeout(20_000),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Preview failed (${res.status})`);
        const text = await res.text();
        if (!cancelled) setHtml(text);
      })
      .catch((err: unknown) => {
        if (!cancelled) setPreviewError(err instanceof Error ? err.message : String(err));
      });
    return () => { cancelled = true; };
  }, [reload]);

  const v = versions.data;

  return (
    <>
      {v && (
        <p className="page-line">
          <strong>Version {v.current.version}</strong> published {day(v.current.publishedAt)}
          {v.versions[0] && <> · {plural(v.versions.find((x) => x.version === v.current.version)?.cookies ?? v.versions[0].cookies, 'cookie')}</>}
          {' · '}{plural(v.versions.length, 'version')}
        </p>
      )}

      <Card title="Public address">
        {banners.length === 0 ? <Empty>No cookie banner.</Empty> : (
          <div className="ck-address">
            {banners.length > 1 && (
              <select value={banner?.id ?? ''} onChange={(e) => setBannerId(e.target.value)} aria-label="Banner">
                {banners.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <input type="text" readOnly className="mono" value={address} aria-label="Public address" onFocus={(e) => e.currentTarget.select()} />
            <button
              className="small"
              onClick={() => navigator.clipboard.writeText(address).then(
                () => toast('Copied.', 'ok'),
                () => toast('Copy failed.', 'bad'),
              )}
            >
              Copy
            </button>
          </div>
        )}
      </Card>

      <Card title="Versions">
        <ErrorNote error={versions.error} />
        {versions.loading && !v && <Loading what="Loading versions" />}
        {v && v.versions.length === 0 && <Empty>No versions published.</Empty>}
        {v && v.versions.length > 0 && (
          <div className="table-scroll">
            <table className="data ck-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Published</th>
                  <th className="num">Cookies</th>
                  <th>Content hash</th>
                </tr>
              </thead>
              <tbody>
                {v.versions.map((row) => (
                  <tr key={row.version}>
                    <td>
                      {row.version}
                      {row.version === v.current.version && <span className="small muted"> · current</span>}
                    </td>
                    <td className="small">{day(row.publishedAt)}</td>
                    <td className="num">{row.cookies}</td>
                    <td className="mono" title={row.contentHash}>{row.contentHash.slice(0, 12)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Preview">
        <ErrorNote error={previewError} />
        {html === null && !previewError && <Loading what="Loading preview" />}
        {html !== null && (
          <iframe className="ck-preview" title="Cookie policy preview" sandbox="" srcDoc={html} />
        )}
      </Card>
    </>
  );
}
